// ============================================================
// DEALIN MVP - Google Analytics Integration
// ============================================================
// GA4 Property: G-7SYRMVBBX2 (Property 526889248)
// Uses gtag.js for tracking
// Respects privacy: minimal cookies, IP anonymization enabled
// ============================================================

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GA_MEASUREMENT_ID = 'G-7SYRMVBBX2';
const GOOGLE_ADS_VENDOR_CONTACT_SEND_TO = 'AW-18028069492/yeAECO3A4ZscEPSEupRD';

// Declare gtag on window
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Load the gtag.js script after first paint (deferred).
// The gtag() stub is already defined in index.html so events queue immediately.
export const initGA = () => {
  if (document.getElementById('ga-script')) return;

  const load = () => {
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_flags: 'SameSite=Strict;Secure',
      send_page_view: false,
    });
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(load, { timeout: 3000 });
  } else {
    setTimeout(load, 1500);
  }
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
) => {
  if (!window.gtag) return;
  
  window.gtag('event', eventName, params);
};

export const trackAdsVendorContactConversion = () => {
  if (!window.gtag) return;

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_VENDOR_CONTACT_SEND_TO,
  });
};

// Predefined events for DEALIN
export const GAEvents = {
  // User actions
  login: () => trackEvent('login', { method: 'email' }),
  signup: (userType: 'vendor' | 'client') => trackEvent('sign_up', { user_type: userType }),
  
  // Vendor interactions
  viewVendorProfile: (vendorId: number) => trackEvent('view_vendor', { vendor_id: vendorId }),
  viewService: (serviceId: number, vendorId: number) => trackEvent('view_service', { service_id: serviceId, vendor_id: vendorId }),
  contactVendor: (vendorId: number) => trackEvent('contact_vendor', { vendor_id: vendorId }),
  startChat: (vendorId: number, context?: string) => trackEvent('start_chat', { vendor_id: vendorId, context: context || 'general' }),
  
  // Tender/Deal actions
  viewTender: (tenderId: number, isBando: boolean) => trackEvent('view_tender', { tender_id: tenderId, is_bando: isBando }),
  applyToTender: (tenderId: number) => trackEvent('apply_tender', { tender_id: tenderId }),
  createTender: (isBando: boolean) => trackEvent('create_tender', { is_bando: isBando }),
  
  // Service actions
  createService: (vendorId: number) => trackEvent('create_service', { vendor_id: vendorId }),
  completeService: (serviceId: number) => trackEvent('complete_service', { service_id: serviceId }),
  
  // Search
  search: (query: string, filters?: Record<string, string>) => trackEvent('search', { search_term: query, ...filters }),
  
  // Reviews
  leaveReview: (vendorId: number, rating: number) => trackEvent('leave_review', { vendor_id: vendorId, rating }),
};

// Component to auto-track page views
export const GoogleAnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  // Initialize GA on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return <>{children}</>;
};

export default GoogleAnalyticsProvider;
